import type { FieldResult, LineItemRow } from '../shared/desktop-api';
import {
  lineItemArithmeticSummary,
  linesMissTheirTotal,
  money,
} from '../shared/line-item-arithmetic';

/** Same arithmetic summary in the workspace and in a popped-out table. A missing
 * amount is not a successful check; accepting an exception is not multiplication.
 * Rows that each add up but together miss the page's subtotal are not "No
 * arithmetic issues" either (invoice RIT-INV-26008, 2026-10-05: a line was left
 * out of the table and the chip said so beside a document held for it). Nor
 * are rows the engine holds the table for (`held`, tableHoldWords): a line
 * whose printed numbers differ from the row read balances, and so does a table
 * whose total this side cannot see missed; the chip says the engine's words. */
export function LineItemArithmeticStatus({
  rows,
  fields = [],
  held = null,
  compact = false,
}: {
  rows: readonly LineItemRow[];
  fields?: readonly FieldResult[];
  compact?: boolean;
  held?: { lineDiffersFromPage?: string; total?: string } | null;
}) {
  const { balanced, accepted, off, missingAmounts } = lineItemArithmeticSummary(rows);
  const totals = linesMissTheirTotal(rows, fields);
  const gap = totals ? Math.abs(totals.sum - totals.total) : 0;
  // The gap is the number to check against the page (review pane, 2026-10-14: "Lines run
  // past the total" gave none); the hover adds both sums.
  const totalsPart = !totals
    ? ''
    : `Lines ${money(gap)} ${totals.sum < totals.total ? 'short of' : 'over'} the ${totals.subtotal ? 'subtotal' : 'total'}`;
  // The engine's table holds the rows here do not show. A total this side already says is not said twice.
  const heldPart = [held?.lineDiffersFromPage ?? '', totals ? '' : (held?.total ?? '')]
    .filter(Boolean)
    .join(' · ');
  const parts = [
    balanced ? `Amounts match · ${balanced}` : '',
    accepted ? `${accepted} accepted` : '',
    off ? `${off} to check` : '',
    missingAmounts
      ? `${missingAmounts} ${missingAmounts === 1 ? 'amount' : 'amounts'} missing`
      : '',
    totalsPart,
    heldPart,
  ].filter(Boolean);
  if (!rows.length) return null;
  const settled = off === 0 && missingAmounts === 0 && !totals && !heldPart;

  const shown =
    compact && !settled
      ? [
          off ? `${off} to check` : '',
          missingAmounts
            ? `${missingAmounts} ${missingAmounts === 1 ? 'amount' : 'amounts'} missing`
            : '',
          totalsPart,
          heldPart,
        ].filter(Boolean)
      : parts;
  const summary = !parts.length
    ? 'Arithmetic not checked'
    : settled
      ? accepted > 0
        ? 'No unresolved arithmetic issues'
        : 'No arithmetic issues'
      : shown.join(' · ');
  // The chip says what is wrong; its hover adds only the two sums when the lines miss the page's total.
  const totalsTitle = totals
    ? `Lines add up to ${money(totals.sum)}; the ${totals.subtotal ? 'subtotal' : 'total'} is ${money(totals.total)}`
    : undefined;
  return (
    <span
      className={`reconciliation-chip ${settled && parts.length ? 'balanced' : ''}`}
      role="status"
      aria-label={`Arithmetic: ${parts.length ? parts.join(' · ') : 'No rows have both quantity and unit price'}`}
      title={totalsTitle}
    >
      <strong>{summary}</strong>
    </span>
  );
}
